import { Grid, Box, Heading, Flex, Text } from '@chakra-ui/react'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { GameRisk } from '@/__generated__/graphql'
import { FormProvider, useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { getRoundNumbers, getUint32 } from '../pkg/rng_v2'
import { sha256 } from '@noble/hashes/sha256'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import { useTranslation } from 'next-i18next/pages'

const schema = yup
  .object({
    gameId: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .label('Game id')
      .min(1),
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),

    quantity: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .min(1)
      .max(100)
      .label('Balls'),
  })
  .required()

export const PlinkoVerify = () => {
  const { t } = useTranslation('common')
  const form = useForm({
    resolver: yupResolver(schema),
  })

  const [state, setState] = useState<{ result: number[]; risk: GameRisk; balls: number } | undefined>()

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }
    const signature = form.getValues('signature').replace('0x', '') as any
    const gameId = form.getValues('gameId')
    const balls = Number(form.getValues('quantity'))

    const randomness = Buffer.from((await sha256(signature)) as any).toString('hex')

    // Default values for verification - in actual game these would come from game settings
    const risk = GameRisk.Medium
    const rows = 16

    const result = getUint32({
      serverKey: Buffer.from(randomness, 'hex'),
      userSeed: Buffer.from(`plinko-${gameId}`),
      count: rows * balls,
    })

    const rounds = getRoundNumbers(result, 2, false)

    const chunks = Array.from({ length: balls }, (_, i) => rounds.slice(i * 16, i * 16 + 16))

    const bucketResults = chunks.map((ballRounds) => ballRounds.reduce((a, b) => a + b, 0))

    setState({
      result: bucketResults,
      risk,
      balls,
    })
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', lg: '260px auto' }}>
      <Box p={5} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <VerifyFormTitle mode="drng" />
          <VerifyFormInput label={t('verify.game-id')} placeholder={t('verify.enter-game-id')} name="gameId" />
          <VerifyFormTextarea
            label={t('verify.signature')}
            placeholder={t('verify.enter-signature')}
            name="signature"
          />
          <VerifyFormInput
            label={t('verify.balls')}
            placeholder={t('verify.enter-balls')}
            name={'quantity'}
            type={'number'}
          />
          <VerifyFormButton onClick={handleClick} />
        </FormProvider>
      </Box>
      <Box p={5} minH={{ base: '0', lg: '375px' }} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={{ base: 2, lg: 0 }}>
          Result
        </Heading>
        {!state ? (
          <EmptyPlaceholder game={Section.PLINKO} />
        ) : (
          <PlinkoVerifyResult result={state.result} risk={state.risk} balls={state.balls} />
        )}
      </Box>
    </Grid>
  )
}

const PlinkoVerifyResult = ({ result, risk, balls }: { result: number[]; risk: GameRisk; balls: number }) => {
  const bucketCount: { [key: number]: number } = {}
  result.forEach((bucket) => {
    bucketCount[bucket] = (bucketCount[bucket] || 0) + 1
  })

  const maxCount = Math.max(...Object.values(bucketCount))

  const multipliers = getMultipliers(risk)

  return (
    <Box h={{ base: 'auto', lg: 'calc(100% - 24px)' }} display="flex" flexDirection="column" justifyContent="center">
      <Flex gap={{ base: 1, md: 2 }} justifyContent="center" mb={2}>
        {Array.from({ length: 17 }).map((_, index) => {
          const count = bucketCount[index] || 0
          const percentage = maxCount > 0 ? count / maxCount : 0
          const isEdge = index <= 2 || index >= 14
          const edgeBonus = isEdge ? 0.2 : 0
          const opacity = count > 0 ? 0.3 + percentage * 0.7 + edgeBonus : 0.3

          const color = getMultiplierColor(multipliers[index], risk)

          return (
            <Flex
              key={index}
              flex={'1 1 0'}
              minW={0}
              maxW={{ base: '30px', md: '40px' }}
              direction="column"
              align="center"
            >
              <Box
                borderBottomRadius="6px"
                border="1px solid"
                borderColor={color}
                borderTop="none"
                bg={color}
                opacity={opacity}
                minH={{ base: '19px', md: '26px' }}
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize={{ base: '10px', md: '12px' }}
                fontWeight={600}
                color="white"
                transition="opacity 0.2s"
                overflow="hidden"
              >
                {count > 0 && <Text>{count}</Text>}
              </Box>
              <Text fontSize={{ base: '9px', md: '10px' }} color="gray.500" mt={{ base: 0.5, md: 1 }} fontWeight={500}>
                {index + 1}
              </Text>
            </Flex>
          )
        })}
      </Flex>
      <Text mt={4} fontSize={'xs'} color="gray.500" textAlign="center">
        {result.map((x) => (x + 1).toString()).join(', ')}
      </Text>
    </Box>
  )
}

const getMultipliers = (risk: GameRisk): number[] => {
  switch (risk) {
    case GameRisk.Low:
      return [16, 9, 2, 1.4, 1.4, 1.2, 1.1, 1, 0.5, 1, 1.1, 1.2, 1.4, 1.4, 2, 9, 16]
    case GameRisk.Medium:
      return [110, 41, 10, 5, 3, 1.5, 1, 0.5, 0.3, 0.5, 1, 1.5, 3, 5, 10, 41, 110]
    case GameRisk.High:
      return [1000, 130, 26, 9, 4, 2, 0.2, 0.2, 0.2, 0.2, 0.2, 2, 4, 9, 26, 130, 1000]
    default:
      return Array(17).fill(1)
  }
}

const getMultiplierColor = (multiplier: number, risk: GameRisk): string => {
  if (risk === GameRisk.Low) {
    if (multiplier >= 16) return '#68F27F'
    if (multiplier >= 9) return '#73DE31'
    if (multiplier >= 2) return '#30C6AC'
    return '#8784B7'
  }

  if (risk === GameRisk.Medium) {
    if (multiplier >= 110) return '#C077F7'
    if (multiplier >= 41) return '#A26BF5'
    if (multiplier >= 10) return '#8E83FB'
    if (multiplier >= 5) return '#7C71F6'
    return '#8784B7'
  }

  if (risk === GameRisk.High) {
    if (multiplier >= 1000) return '#FF5757'
    if (multiplier >= 130) return '#FF7D7D'
    if (multiplier >= 26) return '#E383FB'
    if (multiplier >= 9) return '#C077F7'
    return '#8784B7'
  }

  return '#8784B7'
}
